/**
 * Extract a human-facing error message from a failed API call.
 *
 * The platform API rejects with an axios error whose `response.data` is the
 * backend error envelope, e.g. `{ message: "Invalid zip code",
 * http_error_code: { message, canonical_name, status }, ... }`. Different
 * endpoints surface the message in slightly different shapes, so we check the
 * common ones in order and fall back to a generic message.
 */
export const getApiErrorMessage = (
  error: any,
  fallback = "Something went wrong. Please try again."
): string => {
  const data = error?.response?.data;

  if (data) {
    if (typeof data === "string" && data.trim()) return data.trim();
    if (typeof data.message === "string" && data.message.trim())
      return data.message.trim();
    if (
      typeof data.http_error_code?.message === "string" &&
      data.http_error_code.message.trim()
    )
      return data.http_error_code.message.trim();
    // Some endpoints return a `messages` array (e.g. invite client).
    if (
      Array.isArray(data.messages) &&
      typeof data.messages[0]?.message === "string" &&
      data.messages[0].message.trim()
    )
      return data.messages[0].message.trim();
  }

  if (
    typeof error?.message === "string" &&
    error.message.trim() &&
    error.message !== "Network Error"
  )
    return error.message.trim();

  return fallback;
};

/**
 * The per-field reasons of a 422 validation envelope, e.g.
 *   validation: [{ field: "questions[3000000000075]", value: "a",
 *                  reason: "'WHEN VISITED (MM/DD/YYYY)' must be a date as MM/DD/YYYY" }]
 * Empty when the response carries none.
 */
export const getApiValidationReasons = (error: any): string[] => {
  const validation = error?.response?.data?.validation;
  if (!Array.isArray(validation)) return [];
  return validation
    .map((entry: any) =>
      typeof entry?.reason === "string" ? entry.reason.trim() : ""
    )
    .filter((reason: string) => reason.length > 0);
};
